import { type ReactNode } from 'react';
import { EntityCard } from './EntityCard';
import { CombatStatsBlock } from '../primitives/Stat';
import { FleetIcon } from '../../icons/ui-icons';
import type { CombatStats, ProgressState, ResourceAmounts } from '../../lib/game';

export interface ShipCardProps {
  name: ReactNode;
  /** Role line from the fleet catalogue, e.g. "Leichte Abfangeinheit". */
  role?: ReactNode;
  stats: CombatStats;
  art?: ReactNode;
  owned?: number;
  state?: ProgressState;
  cost?: ResourceAmounts;
  available?: ResourceAmounts;
  /** Build time per unit, in seconds. */
  duration?: number;
  requirement?: ReactNode;
  onBuild?: () => void;
  secondaryAction?: ReactNode;
  className?: string;
}

/**
 * Hangar card for a ship class.
 *
 * Always renders the full hull/shield/damage/cargo block — comparing two hulls
 * is the core decision in the shipyard, and hiding a stat on some cards would
 * break that comparison.
 */
export function ShipCard({
  name,
  role,
  stats,
  art,
  owned,
  state = 'available',
  cost,
  available,
  duration,
  requirement,
  onBuild,
  secondaryAction,
  className,
}: ShipCardProps) {
  return (
    <EntityCard
      name={name}
      description={role}
      art={art}
      icon={<FleetIcon size={40} />}
      owned={owned}
      state={state}
      cost={cost}
      available={available}
      duration={duration}
      requirement={requirement}
      actionLabel="Bauen"
      onAction={onBuild}
      secondaryAction={secondaryAction}
      stats={<CombatStatsBlock stats={stats} dense />}
      className={className}
    />
  );
}

export interface DefenseCardProps extends Omit<ShipCardProps, 'stats'> {
  /** Defence structures have no cargo hold — the type enforces it. */
  stats: Omit<CombatStats, 'cargo'>;
  /** Marks special-purpose systems like the interceptor missile rack. */
  special?: ReactNode;
}

/** Card for a stationary defence structure. */
export function DefenseCard({ stats, special, role, ...rest }: DefenseCardProps) {
  return (
    <ShipCard
      {...rest}
      role={special ?? role}
      stats={{ hull: stats.hull, shield: stats.shield, damage: stats.damage }}
    />
  );
}
